/**
 * CoachContextSidebar — compact three-tile strip above the chat.
 *
 * Shows the operator what's actually wired up while they're talking
 * to the Coach:
 *
 *   · Engine health       — from `useEngineHealth` (healthy / degraded
 *                           / offline). Same state machine as the
 *                           AXIOM chip on the Connectors page.
 *   · Your GIs            — active_gis from `useCommandSnapshot`.
 *   · Available domains   — the four stock domain chips the tree
 *                           engine knows about. When real-mode Coach
 *                           lands, Window 1 will swap this for a
 *                           server-delivered adapter list.
 *
 * Purely display — no interaction wired. The quick-start chips in the
 * ChatPanel remain the canonical way to jump straight to a domain.
 */

import { useTranslation } from 'react-i18next';

import { cn } from '@/utils/cn';
import { useCommandSnapshot, useEngineHealth } from '@/hooks/useCommand';

import type { CoachDomain } from './trees';

const DOMAIN_IDS: CoachDomain[] = ['food', 'medical', 'retail', 'office'];

export function CoachContextSidebar() {
  const { t } = useTranslation('coach');
  const health = useEngineHealth();
  const snapshot = useCommandSnapshot();

  const healthState: HealthState =
    health.data === null
      ? 'offline'
      : health.data === undefined
        ? 'probing'
        : health.data.status;

  const gisLoaded = snapshot.data !== undefined;
  const gisCount = snapshot.data?.stats.active_gis ?? 0;

  return (
    <section
      aria-label={t('context.aria_label')}
      className="grid grid-cols-1 gap-3 sm:grid-cols-3"
    >
      <Tile
        label={t('context.engine.label')}
        value={t(`context.engine.state.${healthState}`)}
        tone={toneForHealth(healthState)}
      />
      <Tile
        label={t('context.gis.label')}
        value={gisLoaded ? String(gisCount) : '—'}
        hint={t('context.gis.hint')}
      />
      <Tile
        label={t('context.domains.label')}
        value={String(DOMAIN_IDS.length)}
        hint={DOMAIN_IDS.map((id) => t(`quickstart.${id}`)).join(' · ')}
      />
    </section>
  );
}

type HealthState = 'offline' | 'probing' | 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
type Tone = 'neutral' | 'success' | 'warn' | 'danger';

function toneForHealth(state: HealthState): Tone {
  switch (state) {
    case 'healthy':
      return 'success';
    case 'degraded':
      return 'warn';
    case 'unhealthy':
      return 'danger';
    default:
      return 'neutral';
  }
}

function Tile({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: Tone;
}) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-border bg-surface px-3 py-2.5">
      <span className="text-[10px] uppercase tracking-wider text-ink-subtle">
        {label}
      </span>
      <span
        className={cn(
          'text-sm font-semibold',
          tone === 'success' && 'text-success',
          tone === 'warn' && 'text-warn',
          tone === 'danger' && 'text-danger',
          tone === 'neutral' && 'text-ink',
        )}
      >
        {value}
      </span>
      {hint ? (
        <span className="truncate text-[11px] text-ink-muted" title={hint}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}
